import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronDown, Tag } from 'lucide-react';
import { FFC_LEDGER_SUBJECTS } from '@/types';

const BUTTON_ACTIVE =
  'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]';
const BUTTON_INACTIVE =
  'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]';
const BUTTON_BASE =
  'inline-flex items-center gap-1.5 h-9 px-3 rounded-[0.50rem] border text-[12px] font-semibold cursor-pointer transition-colors';

interface LedgerFilterBarProps {
  subject?: string;
  onSubjectChange: (subject?: string) => void;
  dateFrom?: string;
  dateTo?: string;
  onDateChange: (from?: string, to?: string) => void;
}

/**
 * Ledger filter buttons — exact clone of the Remittance tab's filter-button
 * pattern (finance/index.tsx `TableFilterButtons`): Type dropdown (the four
 * ffc_* subjects) + Date range dropdown, same active/inactive tokens.
 */
function LedgerFilterBar({
  subject,
  onSubjectChange,
  dateFrom,
  dateTo,
  onDateChange,
}: LedgerFilterBarProps) {
  const { t } = useTranslation();
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [localFrom, setLocalFrom] = useState(dateFrom ?? '');
  const [localTo, setLocalTo] = useState(dateTo ?? '');
  const typeRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (typeRef.current && !typeRef.current.contains(e.target as Node)) {
        setShowTypeDropdown(false);
      }
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) {
        setShowDateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasDateFilter = !!(dateFrom || dateTo);

  return (
    <div className='flex items-center justify-end gap-2 pb-2'>
      {/* Type (subject) filter */}
      <div className='relative' ref={typeRef}>
        <button
          type='button'
          onClick={() => setShowTypeDropdown(!showTypeDropdown)}
          className={`${BUTTON_BASE} ${subject ? BUTTON_ACTIVE : BUTTON_INACTIVE}`}
        >
          <Tag className='h-3.5 w-3.5' />
          {subject ? String(t(`transaction_type_${subject}`)) : String(t('type'))}
          <ChevronDown className='h-3.5 w-3.5' />
        </button>
        {showTypeDropdown ? (
          <div className='absolute start-0 top-full z-50 mt-1 min-w-[200px] rounded-lg border border-[rgb(232,230,227)] bg-white py-1 shadow-lg'>
            <button
              type='button'
              onClick={() => {
                onSubjectChange(undefined);
                setShowTypeDropdown(false);
              }}
              className={`w-full cursor-pointer px-3 py-2 text-start text-[12px] hover:bg-[rgb(245,245,243)] ${!subject ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
            >
              {String(t('all'))}
            </button>
            {FFC_LEDGER_SUBJECTS.map((slug) => (
              <button
                key={slug}
                type='button'
                onClick={() => {
                  onSubjectChange(slug);
                  setShowTypeDropdown(false);
                }}
                className={`w-full cursor-pointer px-3 py-2 text-start text-[12px] hover:bg-[rgb(245,245,243)] ${subject === slug ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
              >
                {String(t(`transaction_type_${slug}`))}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Date filter */}
      <div className='relative' ref={dateRef}>
        <button
          type='button'
          onClick={() => setShowDateDropdown(!showDateDropdown)}
          className={`${BUTTON_BASE} ${hasDateFilter ? BUTTON_ACTIVE : BUTTON_INACTIVE}`}
        >
          <Calendar className='h-3.5 w-3.5' />
          {hasDateFilter ? (
            <bdi>{`${dateFrom || '...'} — ${dateTo || '...'}`}</bdi>
          ) : (
            String(t('date'))
          )}
          <ChevronDown className='h-3.5 w-3.5' />
        </button>
        {showDateDropdown ? (
          <div className='absolute end-0 top-full z-50 mt-1 min-w-[220px] rounded-lg border border-[rgb(232,230,227)] bg-white p-3 shadow-lg'>
            <p className='mb-2 text-[10px] font-bold uppercase tracking-wider text-[rgb(115,123,140)]'>
              {String(t('date_range'))}
            </p>
            <div className='space-y-2'>
              <div>
                <label className='mb-1 block text-[10px] text-[rgb(115,123,140)]'>
                  {String(t('date_from'))}
                </label>
                <input
                  type='date'
                  value={localFrom}
                  onChange={(e) => setLocalFrom(e.target.value)}
                  className='h-8 w-full rounded-lg border border-solid border-[rgb(232,230,227)] px-2 text-[12px] text-[rgb(57,57,60)] focus:outline-none'
                />
              </div>
              <div>
                <label className='mb-1 block text-[10px] text-[rgb(115,123,140)]'>
                  {String(t('date_to'))}
                </label>
                <input
                  type='date'
                  value={localTo}
                  onChange={(e) => setLocalTo(e.target.value)}
                  className='h-8 w-full rounded-lg border border-solid border-[rgb(232,230,227)] px-2 text-[12px] text-[rgb(57,57,60)] focus:outline-none'
                />
              </div>
              <div className='flex items-center gap-2 pt-1'>
                {localFrom || localTo || hasDateFilter ? (
                  <button
                    type='button'
                    onClick={() => {
                      setLocalFrom('');
                      setLocalTo('');
                      onDateChange(undefined, undefined);
                    }}
                    className='h-7 cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold hover:underline'
                    style={{ color: 'hsl(0,72%,51%)' }}
                  >
                    {String(t('clear_all'))}
                  </button>
                ) : null}
                <div className='flex-1' />
                <button
                  type='button'
                  onClick={() => {
                    onDateChange(localFrom || undefined, localTo || undefined);
                    setShowDateDropdown(false);
                  }}
                  className='h-7 cursor-pointer rounded-md border-0 bg-[rgb(237,224,74)] px-3 text-[11px] font-semibold text-[rgb(57,57,60)]'
                >
                  {String(t('apply'))}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default LedgerFilterBar;
